import { Outlet, NavLink, Link } from "react-router";

import { NavBar, Footer, ToggleButton, Loader } from "cdhs-ui-library";
import IMAGE from "../../config/imgConfig";
import { BsPersonCircle } from "react-icons/bs";

import styles from "./layout.module.scss";
import { useAppSelector } from "../../store/store";
import { useAppDispatch } from "../../store/store";
import { auth } from '../../store/user/userSlice';
import { setLang } from '../../store/langSlice';
import { useTranslations, useCurrentBuildLanguage } from "../../contexts/LanguageContext";


function Layout() {
  const t = useTranslations();
  const currentBuildLang = useCurrentBuildLanguage();
  const user = useAppSelector((state) => state.user.user);
  const isDashboardUser = useAppSelector((state) => state.user.isDashboardLaunch) 
  const dispatch = useAppDispatch();
  const isUserLoading = useAppSelector(state => state.user.loading);
  const isSurveyLoading = useAppSelector(state => state.survey.loading);
  const currentLanguage = useAppSelector((state) => state.lang.language);

  const logo = [
    { src: IMAGE.ICON_BUBBLES.url, altText: IMAGE.ICON_BUBBLES.alt },
    { src: IMAGE.NAVBAR_LOGO.url, altText: IMAGE.NAVBAR_LOGO.alt },
  ];


  /** handleLanguageToggle **
  * Handles language switching for the build-time translated site.
  *
  * When a new language is selected, this function:
  * 1. Updates the global language preference in Redux (`state.lang.language`) and
  * `localStorage`. This primarily drives language for dynamic content (e.g., data
  * fetched from a database) and persists user choice.
  * 2. Constructs the new URL by taking the current page's path, removing the old
  * language prefix, and adding the new one.
  * 3. Triggers a full browser navigation to this new URL, which loads the
  * separate, pre-built static site for the chosen language.
  * @param {"EN" | "ES"} newLangValue - The language code the user is switching to.
  */
  const handleLanguageToggle = (newLangValue: "EN" | "ES") => {
    // Only proceed if the language is actually changing
    if (newLangValue !== currentBuildLang) {
      dispatch(setLang(newLangValue));
      localStorage.setItem('preferredLanguage', newLangValue);

      const currentPath = window.location.pathname;

      const oldLangPrefix = `/${currentBuildLang.toLowerCase()}`;

      let basePath = currentPath.startsWith(oldLangPrefix)
        ? currentPath.substring(oldLangPrefix.length)
        : currentPath;
      
      if (basePath.length > 0 && !basePath.startsWith('/')) {
        basePath = '/' + basePath;
      }
      if (basePath === "") {
        basePath = "/";
      }
      const newLangPath = `/${newLangValue.toLowerCase()}`;
      const newUrl = `${window.location.origin}${newLangPath}${basePath}${window.location.search}${window.location.hash}`.replace(/([^:]\/)\/+/g, "$1");
      window.location.href = newUrl;
    }
  };

  return (
    <div className={styles.layoutContainer}>
      <NavBar variant="NYTD" logo={logo}> 
        <NavLink to="/" className={({ isActive }) => (isActive ? `${styles.activeLink} ${styles.mobileLink}` : `${styles.mobileLink}`)}>
          {t('navLinkHome')}
        </NavLink>
        <NavLink to="about" className={({ isActive }) => (isActive ? `${styles.activeLink} ${styles.mobileLink}` : `${styles.mobileLink}`)}>
          {t('navLinkAbout')}
        </NavLink>

        <NavLink to="contact" className={({ isActive }) => (isActive ? `${styles.activeLink} ${styles.mobileLink}` : `${styles.mobileLink}`)}>
          {t('navLinkContact')}
        </NavLink>

        {(user && !isDashboardUser) ? (
          <>
            <NavLink to="/user/profile" className={({ isActive }) => (isActive ? `${styles.activeLink} ${styles.mobileLink}` : `${styles.mobileLink}`)}>
              <BsPersonCircle /> {t('myNytdProfileLink')}
            </NavLink>
            <Link to="/" onClick={() => dispatch(auth({ ...user, LoginAttempt: false }))}>
              {t('logoutButton')}
            </Link>
          </>
        ) :  !isDashboardUser && (
        <NavLink to="user/login" className={({ isActive }) => (isActive ? `${styles.activeLink} ${styles.mobileLink}` : `${styles.mobileLink}`)}>
          {t('navLinkLogIn')}
        </NavLink>
        )}
        <div translate="no" className={`notranslate ${styles.toggle}`}> 
            <ToggleButton
              option1={{ value: "ES", label: t('langLabelSpanish', 'Español') }}
              option2={{ value: "EN", label: t('langLabelEnglish', 'English') }}
              initialState={currentBuildLang || currentLanguage}
              onToggle={handleLanguageToggle}
              variant="NYTD"
            />
        </div>
      </NavBar>

      <main className={styles.mainContent}>
        {isUserLoading || isSurveyLoading ? (
          <div className={styles.spinner}>
            <Loader isLoading={isUserLoading || isSurveyLoading} variant="NYTD" />
          </div>
        ) : (
          <Outlet />
        )}
      </main>
      <Footer
        logo={{ src: IMAGE.FOOTER_LOGO.url, imgAlt: t('alt_footerLogo', IMAGE.FOOTER_LOGO.alt) }}
        logoSize="sm"
        externalLinks={[
          { label: t('navLinkContact'), href: `/${currentLanguage.toLowerCase()}/contact` }, 
          { label: t('footer_accessibilityStatement'), href: "https://co.colorado.gov/accessibility-standards" },
          { label: t("footer_privacyPolicy", "General Notice"), href: "https://www.colorado.gov/privacy-statement" },
        ]}
        copyright={t('footerCopyright')}
        variant="NYTD"
      />
    </div>
  );
}

export default Layout;
